import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  Users, Plus, Trash2, Shield, User, AlertCircle, CheckCircle,
  Eye, EyeOff, Pencil, X, Sparkles
} from 'lucide-react';

function EditUserModal({ u, isHe, onClose, onSave }) {
  const [form, setForm] = useState({
    displayName: u.displayName,
    role: u.role,
    aiAccess: u.aiAccess || false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');
    try {
      const { data } = await api.put(`/auth/users/${u.id}`, form);
      onSave(data);
    } catch (error) {
      setErr(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
            {isHe ? 'עריכת משתמש' : 'Edit User'}
          </h3>
          <button onClick={onClose} className="btn-ghost"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>{u.email}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">{isHe ? 'שם תצוגה' : 'Display Name'}</label>
            <input
              type="text"
              className="input-field"
              value={form.displayName}
              onChange={e => setForm({ ...form, displayName: e.target.value })}
              required
              minLength={2}
            />
          </div>

          <div>
            <label className="label">{isHe ? 'תפקיד' : 'Role'}</label>
            <select
              className="select-field"
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
            >
              <option value="user">{isHe ? 'משתמש' : 'User'}</option>
              <option value="admin">{isHe ? 'מנהל' : 'Admin'}</option>
            </select>
          </div>

          {/* AI Access toggle */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {isHe ? 'גישה לעוזר AI' : 'AI Assistant Access'}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {isHe ? 'יאפשר למשתמש לשוחח עם ה-AI ולייצר ברקודים' : 'Allows user to chat with AI and generate barcodes'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, aiAccess: !form.aiAccess })}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${form.aiAccess ? 'bg-purple-500' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.aiAccess ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {err && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />{err}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? (isHe ? 'שומר...' : 'Saving...') : (isHe ? 'שמור' : 'Save')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {isHe ? 'ביטול' : 'Cancel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const isHe = i18n.language === 'he';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', displayName: '', role: 'user' });
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/auth/users');
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage(null);
    try {
      await api.post('/auth/register', { ...form, language: 'he' });
      setMessage({ type: 'success', text: isHe ? 'המשתמש נוצר בהצלחה' : 'User created successfully' });
      setForm({ email: '', password: '', displayName: '', role: 'user' });
      setShowCreate(false);
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create user' });
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) return;
    if (!window.confirm(isHe ? 'למחוק את המשתמש?' : 'Delete this user?')) return;
    try {
      await api.delete(`/auth/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete user' });
    }
  };

  const handleSaveEdit = (updated) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
    setEditingUser(null);
    setMessage({ type: 'success', text: isHe ? 'המשתמש עודכן' : 'User updated' });
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{isHe ? 'אין הרשאה' : 'Access Denied'}</h2>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {editingUser && (
        <EditUserModal
          u={editingUser}
          isHe={isHe}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveEdit}
        />
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
            <Users className="w-7 h-7 text-indigo-400" />
            {isHe ? 'ניהול משתמשים' : 'User Management'}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {isHe ? 'יצירה וניהול משתמשים במערכת' : 'Create and manage system users'}
          </p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {isHe ? 'משתמש חדש' : 'New User'}
        </button>
      </div>

      {message && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-6 text-sm
          ${message.type === 'success'
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Create User Form */}
      {showCreate && (
        <div className="card p-6 mb-6">
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            {isHe ? 'יצירת משתמש חדש' : 'Create New User'}
          </h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{isHe ? 'שם תצוגה' : 'Display Name'}</label>
              <input type="text" className="input-field" value={form.displayName}
                onChange={e => setForm({ ...form, displayName: e.target.value })} required minLength={2} />
            </div>
            <div>
              <label className="label">{isHe ? 'אימייל' : 'Email'}</label>
              <input type="email" className="input-field" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="label">{isHe ? 'סיסמה' : 'Password'}</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} className="input-field pr-12"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  required minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">{isHe ? 'תפקיד' : 'Role'}</label>
              <select className="select-field" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="user">{isHe ? 'משתמש' : 'User'}</option>
                <option value="admin">{isHe ? 'מנהל' : 'Admin'}</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                {isHe ? 'צור משתמש' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">
                {isHe ? 'ביטול' : 'Cancel'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="card p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0">
                {u.role === 'admin' ? (
                  <Shield className="w-5 h-5 text-amber-400" />
                ) : (
                  <User className="w-5 h-5 text-brand-400" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{u.displayName}</p>
                  <span className={u.role === 'admin' ? 'badge-amber' : 'badge-blue'}>{u.role}</span>
                  {u.aiAccess && u.role !== 'admin' && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                      style={{ background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)' }}>
                      <Sparkles className="w-3 h-3" />
                      AI
                    </span>
                  )}
                </div>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{u.email}</p>
              </div>

              <div className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                {new Date(u.createdAt).toLocaleDateString()}
              </div>

              {u.id !== currentUser.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditingUser(u)} className="btn-ghost" title={isHe ? 'עריכה' : 'Edit'}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(u.id)} className="btn-ghost text-red-400" title={isHe ? 'מחיקה' : 'Delete'}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
